import React, { useMemo, useState } from 'react';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { Button } from 'flowbite-react';
import TypeBadge from '../common/TypeBadge';
import { FiArrowRight } from 'react-icons/fi';

export type WorkMatchContext = {
  kind: 'Task' | 'Outcome' | 'Project';
  id: string;
  tags?: string[];
  projectId?: string | null;
};

type ActorChoice = { type: 'Person'; id: string } | { type: 'Team'; id: string };

interface Props {
  context: WorkMatchContext;
}

const WorkMatchPairForm: React.FC<Props> = ({ context }) => {
  const { database, applyCommand } = useDatabase();
  const [selectedObject, setSelectedObject] = useState<wasm.NodeMatcher | null>(null);
  const [selectedActor, setSelectedActor] = useState<ActorChoice | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const objectOptions = useMemo(() => {
    const base: { key: string; label: string; matcher: wasm.NodeMatcher; type: 'Task' | 'Outcome' | 'Tag' | 'Project' }[] = [];
    if (context.kind === 'Task') {
      base.push({ key: `task:${context.id}`, label: 'This task', matcher: { TaskId: { id: context.id } }, type: 'Task' });
    } else if (context.kind === 'Outcome') {
      base.push({ key: `outcome:${context.id}`, label: 'This outcome', matcher: { OutcomeId: { id: context.id } }, type: 'Outcome' });
    } else if (context.kind === "Project") {
      base.push({ key: `project:${context.id}`, label: 'This project', matcher: { ProjectId: { id: context.id } }, type: 'Project' });
    }
    (context.tags || []).forEach(tag => {
      base.push({ key: `tag:${tag}`, label: `#${tag}`, matcher: { Tag: { tag } }, type: 'Tag' });
    });
    if (context.projectId && database) {
      const project = database.projects()?.[context.projectId];
      const projectName = project ? project.name() : undefined;
      if (projectName) {
        base.push({ key: `project:${context.projectId}`, label: projectName, matcher: { ProjectId: { id: context.projectId } }, type: 'Project' });
      }
    }
    return base;
  }, [context, database]);

  const actorOptions = useMemo(() => {
    const items: { key: string; label: string; actor: ActorChoice; type: 'Person' | 'Team' }[] = [];
    if (database) {
      const people = database.people();
      Object.entries(people).forEach(([id, person]) => {
        items.push({ key: `person:${id}`, label: person.name(), actor: { type: 'Person', id }, type: 'Person' });
      });
      const teams = database.teams?.();
      if (teams) {
        Object.entries(teams).forEach(([id, team]) => {
          items.push({ key: `team:${id}`, label: team.name(), actor: { type: 'Team', id }, type: 'Team' });
        });
      }
    }
    return items;
  }, [database]);

  const canSubmit = !!selectedObject && !!selectedActor;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const actor = selectedActor!.type === 'Person' ? { Person: selectedActor!.id } : { Team: selectedActor!.id };
      const work_match = {
        actor,
        object: selectedObject!,
      };
      const command: wasm.DatabaseCommand = {
        type: 'SetWorkMatch',
        work_match,
        enabled: true,
      };
      await applyCommand(command);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg p-3 border border-cm-border-subtle bg-cm-bg-elevated/40">
      <div className="text-sm font-medium text-cm-text-primary mb-2">Add Match</div>
      <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-start overflow-hidden relative">
        <div className="overflow-hidden">
          <div className="text-xs text-cm-text-muted mb-2">What</div>
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1 overscroll-contain">
            {objectOptions.map(o => {
              const isSelected = selectedObject === o.matcher;
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setSelectedObject(isSelected ? null : o.matcher)}
                  className={`w-full flex items-center justify-start gap-2 rounded-md px-2 py-1.5 text-sm border transition-colors ${
                    isSelected
                      ? 'border-cm-accent-blue bg-cm-accent-blue/10 text-cm-text-primary'
                      : 'border-cm-border-subtle bg-cm-bg-surface text-cm-text-primary hover:border-cm-border-mid hover:bg-cm-bg-hover'
                  }`}
                >
                  <TypeBadge t={o.type} />
                  <span className="truncate flex-1 text-left">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        {/* Pairing visual */}
        <div className="relative flex items-center justify-center h-full select-none">
          {/* vertical divider */}
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-cm-border-subtle" />
          {/* centered arrow with background gap */}
          <div className="relative z-10 inline-flex items-center justify-center rounded-full px-2 py-1 text-cm-text-muted bg-cm-bg-elevated border border-cm-border-subtle">
            <FiArrowRight />
          </div>
        </div>
        <div className="overflow-hidden">
          <div className="text-xs text-cm-text-muted mb-2">Who</div>
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1 overscroll-contain">
            {actorOptions.map(a => {
              const isSelected = !!selectedActor && selectedActor.type === a.actor.type && selectedActor.id === a.actor.id;
              return (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => setSelectedActor(isSelected ? null : a.actor)}
                  className={`w-full flex items-center justify-start gap-2 rounded-md px-2 py-1.5 text-sm border transition-colors ${
                    isSelected
                      ? 'border-cm-accent-blue bg-cm-accent-blue/10 text-cm-text-primary'
                      : 'border-cm-border-subtle bg-cm-bg-surface text-cm-text-primary hover:border-cm-border-mid hover:bg-cm-bg-hover'
                  }`}
                >
                  <TypeBadge t={a.type} />
                  <span className="truncate flex-1 text-left">{a.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-4">
        <Button color="blue" size="md" onClick={onSubmit} disabled={!canSubmit || submitting} className="w-full font-semibold">
          Set match
        </Button>
      </div>
    </div>
  );
};

export default WorkMatchPairForm;
